import {
  GlobalOutlined,
  LogoutOutlined,
  OrderedListOutlined,
  ThunderboltOutlined,
  UserOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { history, setLocale, useModel, useRequest } from '@umijs/max';
import { Avatar, Button, Divider, Popover, Segmented, message } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { loginModal } from '@/components/LoginModal';
import { paymentModal } from '@/components/PaymentModal';
import {
  DASHBOARD_DATA_CACHE_KEY,
  extractDashboardLedgerBalance,
  fetchDashboardData,
} from '@/services/dashboard';
import { outLogin } from '@/services/auth';
import { getPlans } from '@/services/payment';
import { normalizePlanList } from '@/pages/payment/utils';
import { formatLocaleMessage as t, getAppLocale } from '@/utils/i18n';

const LANGUAGE_LABELS = {
  'zh-CN': '中',
  'en-US': 'EN',
} as const;

const useStyles = createStyles(({ token, css }) => {
  const borderColor = `color-mix(in srgb, ${token.colorBorderSecondary} 88%, ${token.colorBgContainer})`;
  const footerBackground = 'transparent';
  const hoverBackground = `color-mix(in srgb, ${token.colorFillSecondary} 74%, ${token.colorBgContainer})`;
  const subtleBackground = `color-mix(in srgb, ${token.colorBgContainer} 84%, ${token.colorFillQuaternary})`;
  const focusRing = `0 0 0 2px color-mix(in srgb, ${token.colorPrimaryBg} 75%, transparent)`;

  return {
    root: css`
      width: 100%;
      margin: 0;
      padding: 0;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 84%, transparent);
      background: ${footerBackground};
      overflow: hidden;
    `,
    collapsedRoot: css`
      padding: 10px 0 12px;
      border-top: 1px solid color-mix(in srgb, ${borderColor} 84%, transparent);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      background: ${footerBackground};
    `,
    guestRow: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      width: 100%;
      min-width: 0;
      padding: 12px 16px;
      background: transparent;
      box-sizing: border-box;
    `,
    guestMeta: css`
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    guestTitle: css`
      font-size: 14px;
      line-height: 19px;
      font-weight: 500;
      color: ${token.colorTextHeading};
    `,
    guestDesc: css`
      font-size: 12px;
      line-height: 16px;
      color: ${token.colorTextSecondary};
    `,
    guestPreferences: css`
      display: flex;
      flex-direction: column;
      gap: 8px;
      padding: 0 16px 14px;
    `,
    footerActionButton: css`
      height: 32px;
      padding-inline: 14px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 500;
      box-shadow: none;
      flex-shrink: 0;
    `,
    accountTrigger: css`
      width: 100%;
      min-width: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 12px 16px;
      background: transparent;
      box-sizing: border-box;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: inset ${focusRing};
      }
    `,
    accountLead: css`
      display: flex;
      align-items: center;
      gap: 9px;
      min-width: 0;
      flex: 1;
    `,
    accountMeta: css`
      display: flex;
      flex-direction: column;
      gap: 1px;
      min-width: 0;
      text-align: left;
    `,
    accountName: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      line-height: 19px;
      font-weight: 500;
      color: ${token.colorTextHeading};
    `,
    accountSubtitle: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      line-height: 16px;
      color: ${token.colorTextSecondary};
    `,
    accountTrail: css`
      display: inline-flex;
      align-items: center;
      gap: 0;
      flex-shrink: 0;
    `,
    planBadge: css`
      display: inline-flex;
      align-items: center;
      justify-content: flex-end;
      max-width: 116px;
      min-width: 0;
      color: color-mix(in srgb, ${token.colorPrimary} 82%, ${token.colorText});
      font-size: 12px;
      line-height: 16px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
    triggerArrow: css`
      display: none;
    `,
    expandedAvatar: css`
      background: ${token.colorPrimary};
      flex-shrink: 0;
    `,
    collapsedButton: css`
      width: 40px;
      height: 40px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorTextSecondary};
      cursor: pointer;
      transition: background-color 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }

      &:focus-visible {
        outline: none;
        box-shadow: ${focusRing};
      }
    `,
    collapsedAvatar: css`
      background: ${token.colorPrimary};
    `,
    popoverBody: css`
      width: 290px;
      padding: 6px;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    localePopoverBody: css`
      width: 240px;
      padding: 6px;
      display: flex;
      flex-direction: column;
      gap: 8px;
    `,
    popoverHeader: css`
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px 8px 4px;
    `,
    popoverHeaderMeta: css`
      min-width: 0;
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    popoverTitle: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 15px;
      line-height: 22px;
      font-weight: 500;
      color: ${token.colorTextHeading};
    `,
    popoverSubtitle: css`
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextSecondary};
    `,
    popoverDivider: css`
      margin: 4px 0;
    `,
    balancePanel: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    balanceMeta: css`
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    `,
    balanceLabel: css`
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextSecondary};
    `,
    balanceValue: css`
      font-size: 18px;
      line-height: 24px;
      font-weight: 600;
      color: ${token.colorTextHeading};
      letter-spacing: -0.02em;
    `,
    rechargeButton: css`
      height: 32px;
      padding-inline: 12px;
      border-radius: 999px;
      box-shadow: none;
      font-size: 12px;
      font-weight: 500;
    `,
    sectionTitle: css`
      padding: 2px 8px 0;
      font-size: 11px;
      line-height: 18px;
      font-weight: 500;
      letter-spacing: 0.02em;
      text-transform: uppercase;
      color: ${token.colorTextTertiary};
    `,
    settingBlock: css`
      display: flex;
      flex-direction: column;
      gap: 10px;
      padding: 8px;
      border-radius: 14px;
      background: ${subtleBackground};
    `,
    settingRow: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    `,
    settingLead: css`
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      text-align: left;
      line-height: 20px;
      color: ${token.colorText};
    `,
    settingIcon: css`
      color: ${token.colorTextSecondary};
      font-size: 14px;
    `,
    actionList: css`
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding-top: 2px;
    `,
    actionButton: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      min-height: 40px;
      // padding: 0 10px;
      border: none;
      border-radius: 12px;
      background: transparent;
      color: ${token.colorText};
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: ${hoverBackground};
      }
    `,
    actionLead: css`
      display: inline-flex;
      align-items: center;
      text-align: left;
      gap: 8px;
      min-width: 0;
      font-size: 13px;
      line-height: 20px;
    `,
    actionMeta: css`
      font-size: 12px;
      line-height: 18px;
      color: ${token.colorTextTertiary};
    `,
  };
});

const SiderFooter: React.FC<{ collapsed?: boolean }> = ({ collapsed }) => {
  const { styles } = useStyles();
  const { initialState, setInitialState } = useModel('@@initialState');
  const [accountPopoverOpen, setAccountPopoverOpen] = React.useState(false);
  const [guestLocalePopoverOpen, setGuestLocalePopoverOpen] =
    React.useState(false);

  const isLoggedIn = !!initialState?.currentUser;
  const user = initialState?.currentUser?.user;
  const userId = user?.id;
  const currentLocale = getAppLocale();

  const { data: dashboardData } = useRequest(
    () => fetchDashboardData({ skipErrorHandler: true }),
    {
      ready: Boolean(userId),
      cacheKey: DASHBOARD_DATA_CACHE_KEY,
      staleTime: 5_000,
      refreshOnWindowFocus: false,
    },
  );
  const { data: planList } = useRequest(
    () => getPlans({ skipErrorHandler: true }),
    {
      ready: Boolean(userId),
      cacheKey: 'payment-plans',
      staleTime: 15_000,
      refreshOnWindowFocus: false,
      formatResult: normalizePlanList,
    },
  );

  const balance = React.useMemo(() => {
    if (!userId) {
      return 0;
    }
    return extractDashboardLedgerBalance(dashboardData);
  }, [dashboardData, userId]);

  const balanceText = React.useMemo(() => {
    const amount = Number(balance || 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }

    return new Intl.NumberFormat(currentLocale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, [balance, currentLocale]);

  const userName =
    user?.name || t('components.siderFooter.notLoggedIn', 'Not signed in');
  const accountSubtitle =
    user?.account ||
    t('components.siderFooter.personalAccount', 'Personal account');
  const subscriptionLevel = React.useMemo(() => {
    const currentSubscription = planList?.currentSubscription;
    const rawPlan =
      currentSubscription?.planName ||
      currentSubscription?.planCode ||
      user?.plan ||
      user?.subscriptionPlan ||
      user?.planName ||
      user?.levelName ||
      user?.vipLevelName ||
      user?.memberLevel;

    const normalized = String(rawPlan || '').trim();
    return normalized || t('components.siderFooter.freePlan', 'Free');
  }, [
    planList?.currentSubscription,
    user?.levelName,
    user?.memberLevel,
    user?.plan,
    user?.planName,
    user?.subscriptionPlan,
    user?.vipLevelName,
  ]);

  const closePopover = () => setAccountPopoverOpen(false);

  const handleRecharge = (defaultTab: 'plans' | 'recharge' = 'recharge') => {
    closePopover();
    paymentModal.show({ defaultTab });
  };

  const loginOut = async () => {
    localStorage.removeItem('token');
    await outLogin();
    message.success(
      t('components.siderFooter.logoutSuccess', 'Logged out successfully'),
    );
    history.replace('/home');
  };

  const handleLogout = async () => {
    closePopover();

    flushSync(() => {
      setInitialState((state) => ({ ...state, currentUser: undefined }));
    });

    await loginOut();
  };

  const handleLocaleChange = (value: string | number) => {
    const nextLocale = String(value);
    if (nextLocale !== currentLocale) {
      setGuestLocalePopoverOpen(false);
      setLocale(nextLocale, false);
    }
  };

  /**
   * 语言偏好不能依赖登录态。
   * 游客第一次进入站点时，也需要在不打开登录弹窗的前提下把界面切到可读语言，
   * 因此这里抽成共享区块，供登录态弹层、未登录展开态和折叠态复用同一套切换逻辑。
   */
  const localePreferenceBlock = (
    <>
      <div className={styles.sectionTitle}>
        {t('components.siderFooter.preferences', 'Preferences')}
      </div>
      <div className={styles.settingBlock}>
        <div className={styles.settingRow}>
          <span className={styles.settingLead}>
            <GlobalOutlined className={styles.settingIcon} />
            <span>{t('components.siderFooter.language', 'Language')}</span>
          </span>
          <Segmented
            value={currentLocale}
            onChange={handleLocaleChange}
            options={Object.entries(LANGUAGE_LABELS).map(([value, label]) => ({
              value,
              label,
            }))}
          />
        </div>
      </div>
    </>
  );

  const accountPopoverContent = (
    <div className={styles.popoverBody}>
      <div className={styles.popoverHeader}>
        <Avatar
          size={36}
          icon={<UserOutlined />}
          src={user?.avatar}
          className={styles.expandedAvatar}
        />
        <div className={styles.popoverHeaderMeta}>
          <div className={styles.popoverTitle}>{userName}</div>
          <div className={styles.popoverSubtitle}>{accountSubtitle}</div>
        </div>
      </div>

      <Divider className={styles.popoverDivider} />

      <div className={styles.balancePanel}>
        <div className={styles.balanceMeta}>
          <div className={styles.balanceLabel}>
            <ThunderboltOutlined />
            <span>{t('components.siderFooter.credits', 'Credits')}</span>
          </div>
          <div className={styles.balanceValue}>{balanceText}</div>
        </div>
        <Button
          className={styles.rechargeButton}
          onClick={() => handleRecharge('recharge')}
        >
          {t('components.siderFooter.recharge', 'Recharge')}
        </Button>
      </div>

      <Divider className={styles.popoverDivider} />

      {localePreferenceBlock}

      <Divider className={styles.popoverDivider} />

      <div className={styles.actionList}>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => handleRecharge('plans')}
        >
          <span className={styles.actionLead}>
            <WalletOutlined className={styles.settingIcon} />
            <span>
              {t(
                'components.siderFooter.subscriptionAndRecharge',
                'Subscription & Recharge',
              )}
            </span>
          </span>
          <span className={styles.actionMeta}>
            {t('components.siderFooter.balanceValue', '{amount}', {
              amount: balanceText,
            })}
          </span>
        </button>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => { closePopover(); history.push('/payment/orders'); }}
        >
          <span className={styles.actionLead}>
            <OrderedListOutlined className={styles.settingIcon} />
            <span>{t('components.siderFooter.transactionHistory', 'Transaction History')}</span>
          </span>
        </button>
        <button
          type="button"
          className={styles.actionButton}
          onClick={handleLogout}
        >
          <span className={styles.actionLead}>
            <LogoutOutlined className={styles.settingIcon} />
            <span>{t('components.siderFooter.logout', 'Logout')}</span>
          </span>
        </button>
      </div>
    </div>
  );

  const guestLocalePopoverContent = (
    <div className={styles.localePopoverBody}>{localePreferenceBlock}</div>
  );

  if (collapsed) {
    return (
      <div className={styles.collapsedRoot}>
        {isLoggedIn ? (
          <Popover
            trigger="click"
            placement="topRight"
            open={accountPopoverOpen}
            onOpenChange={setAccountPopoverOpen}
            content={accountPopoverContent}
            styles={{ body: { padding: 0 } }}
          >
            <button
              type="button"
              className={styles.collapsedButton}
              title={userName}
            >
              <Avatar
                size={22}
                icon={<UserOutlined />}
                src={user?.avatar}
                className={styles.collapsedAvatar}
              />
            </button>
          </Popover>
        ) : (
          <>
            <Popover
              trigger="click"
              placement="topRight"
              open={guestLocalePopoverOpen}
              onOpenChange={setGuestLocalePopoverOpen}
              content={guestLocalePopoverContent}
              styles={{ body: { padding: 0 } }}
            >
              <button
                type="button"
                className={styles.collapsedButton}
                title={t('components.siderFooter.language', 'Language')}
                aria-label={t('components.siderFooter.language', 'Language')}
              >
                <GlobalOutlined style={{ fontSize: 16 }} />
              </button>
            </Popover>
            <button
              type="button"
              className={styles.collapsedButton}
              onClick={() => loginModal.show()}
              title={t('components.siderFooter.login', 'Login')}
              aria-label={t('components.siderFooter.login', 'Login')}
            >
              <UserOutlined style={{ fontSize: 16 }} />
            </button>
          </>
        )}
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className={styles.root}>
        <div className={styles.guestRow}>
          <div className={styles.guestMeta}>
            <div className={styles.guestTitle}>
              {t(
                'components.siderFooter.loginPromptTitle',
                'Get responses tailored to you',
              )}
            </div>
            <div className={styles.guestDesc}>
              {t(
                'components.siderFooter.loginPromptDesc',
                'Log in to sync chats, access credits, and keep your preferences consistent.',
              )}
            </div>
          </div>
          <Button
            className={styles.footerActionButton}
            onClick={() => loginModal.show()}
          >
            {t('components.siderFooter.login', 'Login')}
          </Button>
        </div>
        <Divider className={styles.popoverDivider} />
        <div className={styles.guestPreferences}>{localePreferenceBlock}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Popover
        trigger="click"
        placement="topRight"
        open={accountPopoverOpen}
        onOpenChange={setAccountPopoverOpen}
        content={accountPopoverContent}
        styles={{ body: { padding: 0 } }}
      >
        <div
          className={styles.accountTrigger}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setAccountPopoverOpen((open) => !open);
            }
          }}
        >
          <span className={styles.accountLead}>
            <Avatar
              size={30}
              icon={<UserOutlined />}
              src={user?.avatar}
              className={styles.expandedAvatar}
            />
            <span className={styles.accountMeta}>
              <span className={styles.accountName}>{userName}</span>
              <span className={styles.accountSubtitle}>{accountSubtitle}</span>
            </span>
          </span>
          <span className={styles.accountTrail}>
            <span className={styles.planBadge} title={subscriptionLevel}>
              {subscriptionLevel}
            </span>
          </span>
        </div>
      </Popover>
    </div>
  );
};

export default SiderFooter;
